import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { ErrorPageComponent } from '@shared/error-page/error-page.component';
import { TokenExchangeEvents } from '@shared/models/token-exchange-events';
import { filter } from 'rxjs';

/**
 * Wraps a screen's content and swaps it for the error page when the token exchange
 * fails.
 *
 * Without it a failed exchange leaves the screen rendered but empty: every request
 * is either held by `TokenRaceConditionInterceptor` or answered `403`, so the user
 * sees a skeleton that never fills and no explanation. Same component
 * `serasa-cl-webcomponents` uses, for the same reason.
 *
 * Content is projected, so the screen keeps owning its own markup — the wrapper
 * only decides whether to render it.
 */
@Component({
  selector: 'failure-wrapper',
  standalone: true,
  imports: [ErrorPageComponent],
  templateUrl: './failure-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FailureWrapperComponent implements OnInit {
  /**
   * Session events, the source of the failure signal.
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Destroy reference, which unsubscribes with the component.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Whether the exchange failed.
   */
  public readonly failed = signal(false);

  /**
   * Subscribes to the failure event.
   *
   * A signal rather than an `AsyncPipe` over the subject: `eventsSubject$` is a
   * plain `Subject`, so a late subscriber (the wrapper of a screen the router
   * activates after the failure) would never receive the event. The signal keeps
   * the state instead of replaying the stream.
   */
  public ngOnInit(): void {
    this.tokens.eventsSubject$
      .pipe(
        filter((event) => event['event'] === TokenExchangeEvents.TOKEN_EXCHANGED_FAILED),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.failed.set(true));
  }
}
